/**
 * PublicArtifactList
 *
 * Compact, read-only list of the share's artifacts. When more than one kind is
 * shared it shows pill tabs: the "All" tab offers a client-side name search
 * across every kind, while each per-kind tab exposes that kind's server-side
 * viewer filters. Selecting a row reports the selection up to the page (which
 * opens the detail panel and can fly the map).
 *
 * @module components/share/public/PublicArtifactList
 */
import { memo, useMemo, useState } from 'react';
import { Group, Stack, Text } from '@mantine/core';
import { IconLayoutSidebarLeftCollapse } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { AppScrollArea } from '@/components/ui/AppScrollArea';
import type { ScrollGutterToken } from '@/components/ui/AppScrollArea';
import { EmptyState } from '@/components/common/EmptyState';
import { PillTabs } from '@/components/ui/PillTabs';
import type { PillTab } from '@/components/ui/PillTabs/PillTabs.types';
import { ToolbarIconButton } from '@/components/ui/ToolbarIconButton';
import { TOOLBOX_ICON_SIZE } from '@/constants/ui';
import { isArtifactEnabled } from '@/config/features';
import type {
  PublicShareConfig,
  PublicShareListData,
  ShareViewerFilters,
} from '@/types/share';

import {
  itemToDescriptor,
  type PublicArtifactDescriptor,
} from '../usePublicArtifactSelection';
import { PublicListSectionHeader } from './parts/PublicListSectionHeader';
import { PublicListToolbar } from './parts/PublicListToolbar';
import { PublicNoteRow } from './parts/PublicNoteRow';
import { PublicObstructionRow } from './parts/PublicObstructionRow';
import { PublicTaskRow } from './parts/PublicTaskRow';
import { KIND_LABEL_KEYS } from './parts/publicListKindMeta';
import type {
  PublicListKind,
  PublicListTab,
} from './parts/shareViewerFilterUtils';
import styles from './PublicArtifactList.module.css';

interface PublicArtifactListProps {
  config: PublicShareConfig;
  listData: PublicShareListData;
  viewerFilters: ShareViewerFilters;
  onViewerFiltersChange: (filters: ShareViewerFilters) => void;
  onSelect: (descriptor: PublicArtifactDescriptor) => void;
  onCollapse?: () => void;
  /**
   * Gutter token negated by the scroll area's full-bleed bar — MUST match the
   * host's inline padding so the bar reaches its edge. Defaults to `md` (the
   * embed SlidePanel); the shared-map sidebar overrides it to `sm`.
   */
  listGutter?: ScrollGutterToken;
}

/**
 * Case-insensitive title match. Returns the input unchanged when the query is
 * empty so the "All" tab shows everything by default.
 */
function filterByTitle<TItem extends { title: string }>(
  items: TItem[],
  query: string
): TItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) {
    return items;
  }
  return items.filter((item) =>
    item.title.toLowerCase().includes(normalizedQuery)
  );
}

export const PublicArtifactList = memo(function PublicArtifactList({
  config,
  listData,
  viewerFilters,
  onViewerFiltersChange,
  onSelect,
  onCollapse,
  listGutter = 'md',
}: PublicArtifactListProps) {
  const { t } = useTranslation();

  // Obstructions follow the build-time feature flag, mirroring LayersPanel:
  // when disabled they drop out of the tabs, counts, rows and per-kind filter.
  const obstructionsEnabled =
    config.includeObstructions && isArtifactEnabled('obstructions');

  const enabledKinds = useMemo<PublicListKind[]>(() => {
    const kinds: PublicListKind[] = [];
    if (config.includeTasks) kinds.push('task');
    if (config.includeNotes) kinds.push('note');
    if (obstructionsEnabled) kinds.push('obstruction');
    return kinds;
  }, [config.includeTasks, config.includeNotes, obstructionsEnabled]);

  const showTabs = enabledKinds.length > 1;

  const [activeTab, setActiveTab] = useState<PublicListTab>(
    showTabs ? 'all' : (enabledKinds[0] ?? 'all')
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Counts reflect the post-server-filter result sizes already present in `listData`.
  const taskCount = listData.tasks.length;
  const noteCount = listData.notes.length;
  const obstructionCount = obstructionsEnabled
    ? listData.obstructions.length
    : 0;
  const total = taskCount + noteCount + obstructionCount;

  // Client-side name search is intentional here: the public LIST endpoint
  // returns a complete, non-paginated snapshot, there is no unified server-side
  // search, and obstructions have no server-side name search at all. The
  // "server-side filtering only" rule targets paginated entity lists, so it
  // does not apply to this snapshot.
  const searched = useMemo(
    () => ({
      tasks: filterByTitle(listData.tasks, searchQuery),
      notes: filterByTitle(listData.notes, searchQuery),
      obstructions: obstructionsEnabled
        ? filterByTitle(listData.obstructions, searchQuery)
        : [],
    }),
    [listData, searchQuery, obstructionsEnabled]
  );

  const tabs = useMemo<PillTab<PublicListTab>[]>(() => {
    const countByKind: Record<PublicListKind, number> = {
      task: taskCount,
      note: noteCount,
      obstruction: obstructionCount,
    };
    return [
      { value: 'all', labelKey: 'share.public.tabs.all', count: total },
      ...enabledKinds.map((kind) => ({
        value: kind,
        labelKey: KIND_LABEL_KEYS[kind],
        count: countByKind[kind],
      })),
    ];
  }, [enabledKinds, total, taskCount, noteCount, obstructionCount]);

  // The name search applies on every tab; the active tab just selects which
  // kinds are visible. Each `visibleX` is already empty when its tab is hidden,
  // so the JSX can render it directly without an extra guard.
  const isAllTab = activeTab === 'all';
  const visibleTasks = isAllTab || activeTab === 'task' ? searched.tasks : [];
  const visibleObstructions =
    isAllTab || activeTab === 'obstruction' ? searched.obstructions : [];
  const visibleNotes = isAllTab || activeTab === 'note' ? searched.notes : [];

  const visibleCount =
    visibleTasks.length + visibleObstructions.length + visibleNotes.length;

  return (
    <Stack gap="sm" className={styles.container}>
      <Group justify="space-between">
        <Text fw={600}>{t('share.public.listTitle')}</Text>
        {onCollapse && (
          <ToolbarIconButton
            icon={<IconLayoutSidebarLeftCollapse size={TOOLBOX_ICON_SIZE} />}
            tooltip={t('share.public.collapseList')}
            onClick={onCollapse}
            variant="transparent"
          />
        )}
      </Group>

      {showTabs && (
        <PillTabs<PublicListTab>
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          variant="compact"
          flush
        />
      )}

      <PublicListToolbar
        activeTab={activeTab}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        viewerFilters={viewerFilters}
        onViewerFiltersChange={onViewerFiltersChange}
        config={config}
      />

      {visibleCount === 0 ? (
        <EmptyState title={t('share.public.empty')} />
      ) : (
        <AppScrollArea fill fullBleedGutter={listGutter}>
          <Stack gap="3xs" className={styles.rows}>
            {isAllTab && visibleTasks.length > 0 && (
              <PublicListSectionHeader
                kind="task"
                count={visibleTasks.length}
              />
            )}
            {visibleTasks.map((task) => (
              <PublicTaskRow
                key={task.id}
                task={task}
                onClick={() => onSelect(itemToDescriptor('task', task))}
              />
            ))}
            {isAllTab && visibleObstructions.length > 0 && (
              <PublicListSectionHeader
                kind="obstruction"
                count={visibleObstructions.length}
              />
            )}
            {visibleObstructions.map((obstruction) => (
              <PublicObstructionRow
                key={obstruction.id}
                obstruction={obstruction}
                onClick={() =>
                  onSelect(itemToDescriptor('obstruction', obstruction))
                }
              />
            ))}
            {isAllTab && visibleNotes.length > 0 && (
              <PublicListSectionHeader
                kind="note"
                count={visibleNotes.length}
              />
            )}
            {visibleNotes.map((note) => (
              <PublicNoteRow
                key={note.id}
                note={note}
                onClick={() => onSelect(itemToDescriptor('note', note))}
              />
            ))}
          </Stack>
        </AppScrollArea>
      )}
    </Stack>
  );
});
